import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

/**
 * Two cleanup jobs behind one panel:
 *  - "link": estimates still carrying a text-only customer, matched to a directory record
 *  - "import": customer names Sales already uses that the directory has never heard of
 */
export default function DirectoryLinkPanel({ mode, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [choice, setChoice] = useState({});   // estimate_id -> company_id
  const [picked, setPicked] = useState([]);   // sales names to import
  const [type, setType] = useState("gc");
  const [busy, setBusy] = useState(false);
  // Its own full list: the table behind this panel may be filtered down to a handful.
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState("");

  useEffect(() => {
    if (mode !== "link") return;
    estApi.getCompanies({ limit: 1000 })
      .then((d) => setCompanies(d.companies || []))
      .catch(() => setCompanies([]));
  }, [mode]);

  useEffect(() => {
    (mode === "link" ? estApi.directoryUnlinked() : estApi.directorySalesNames(400))
      .then((d) => {
        setRows(d.rows);
        if (mode === "link") {
          const pre = {};
          d.rows.forEach((r) => { if (r.suggestion) pre[r.id] = r.suggestion.id; });
          setChoice(pre);
        }
      });
  }, [mode]);

  const companyOptions = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    const list = q ? companies.filter((c) => c.name.toLowerCase().includes(q)) : companies;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, companySearch]);

  const unmatched = useMemo(
    () => (rows || []).filter((r) => mode === "import" && !r.company_id), [rows, mode]);

  const linkAll = async () => {
    const pairs = Object.entries(choice).filter(([, cid]) => cid);
    if (!pairs.length) return toast.error("Nothing chosen to link");
    setBusy(true);
    try {
      for (const [estimate_id, company_id] of pairs) {
        await estApi.directoryLink(estimate_id, company_id);
      }
      toast.success(`${pairs.length} estimate(s) linked`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    } finally {
      setBusy(false);
    }
  };

  const importPicked = async () => {
    if (!picked.length) return toast.error("Tick the names to bring across");
    setBusy(true);
    try {
      const res = await estApi.directoryImportNames(picked, type);
      toast.success(`${res.created} company record(s) created`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6"
         onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="directory-panel">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">
              {mode === "link" ? "Link customers to the directory" : "Bring Sales names into the directory"}
            </h2>
            <p className="text-[11px] text-slate-500">
              {mode === "link"
                ? "These estimates carry a customer as plain text. Pick the real record so contacts and proposal addresses fill in."
                : "Names already used on Sales projects. Tick the ones worth a directory record — nothing is duplicated."}
            </p>
          </div>
          <button onClick={onClose} data-testid="directory-panel-close"
                  className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!rows && <Loader2 className="mx-auto my-8 h-4 w-4 animate-spin text-slate-400" />}

          {rows && mode === "link" && (
            <div className="mb-2 flex items-center gap-2">
              <input value={companySearch} onChange={(e) => setCompanySearch(e.target.value)}
                     placeholder={`Filter the ${companies.length} companies in the dropdowns…`}
                     data-testid="link-company-search"
                     className="h-7 w-72 rounded-md border border-slate-200 px-2 text-xs" />
              {companySearch && (
                <button onClick={() => setCompanySearch("")}
                        className="text-[11px] text-slate-400 hover:underline">clear</button>
              )}
            </div>
          )}

          {rows && mode === "link" && (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1.5 text-left">Estimate</th>
                  <th className="py-1.5 text-left">Text customer</th>
                  <th className="py-1.5 text-left">Link to</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100"
                      data-testid={`link-row-${r.id}`}>
                    <td className="py-1.5 pr-2 text-slate-700">
                      {r.estimate_number ? `${r.estimate_number} · ` : ""}{r.project_name}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.gc_name}</td>
                    <td className="py-1.5">
                      <select value={choice[r.id] || ""} data-testid={`link-select-${r.id}`}
                              onChange={(e) => setChoice((c) => ({ ...c, [r.id]: e.target.value }))}
                              className="h-7 w-full rounded-md border border-slate-200 px-2 text-xs">
                        <option value="">— skip —</option>
                        {companyOptions.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {r.suggestion && (
                        <span className="text-[10px] text-emerald-600">
                          suggested: {r.suggestion.name}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={3} className="py-6 text-center text-slate-400">
                    Every estimate points at a real company. Nothing to do.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}

          {rows && mode === "import" && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Bring them in as</span>
                <select value={type} onChange={(e) => setType(e.target.value)}
                        data-testid="import-type"
                        className="h-7 rounded-md border border-slate-200 px-2 text-xs">
                  <option value="gc">General Contractor</option>
                  <option value="architect">Architect</option>
                  <option value="owner">Owner</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="supplier">Supplier</option>
                  <option value="subcontractor">Subcontractor</option>
                </select>
                <button onClick={() => setPicked(unmatched.map((r) => r.name))}
                        data-testid="import-select-all"
                        className="text-[11px] text-[#007BFF] hover:underline">
                  Select all {unmatched.length}
                </button>
                {picked.length > 0 && (
                  <button onClick={() => setPicked([])}
                          className="text-[11px] text-slate-400 hover:underline">Clear</button>
                )}
              </div>
              <div className="space-y-0.5">
                {unmatched.map((r) => (
                  <label key={r.name} data-testid={`import-row-${r.name}`}
                         className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50">
                    <input type="checkbox" checked={picked.includes(r.name)}
                           onChange={(e) => setPicked((p) => e.target.checked
                             ? [...p, r.name] : p.filter((n) => n !== r.name))} />
                    <span className="text-xs text-slate-700">{r.name}</span>
                    <span className="text-[10px] text-slate-400">
                      {r.project_count} project{r.project_count === 1 ? "" : "s"}
                    </span>
                    {r.suggestion && (
                      <span className="ml-auto text-[10px] text-amber-600">
                        looks like "{r.suggestion.name}" — skip if it is the same firm
                      </span>
                    )}
                  </label>
                ))}
                {!unmatched.length && (
                  <p className="py-6 text-center text-slate-400">
                    The directory already covers every name Sales uses.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          {mode === "link" ? (
            <Button size="sm" onClick={linkAll} disabled={busy} data-testid="directory-link-confirm">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Link chosen
            </Button>
          ) : (
            <Button size="sm" onClick={importPicked} disabled={busy || !picked.length}
                    data-testid="directory-import-confirm">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Add {picked.length || ""} to directory
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
